import { Color, Spacing } from '@persona-id/design-tokens';
import { ComponentPropsWithoutRef, ReactChild, ReactElement } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';

const Container = styled('div')<Pick<ClickableItemProps, 'disabled'>>`
  ${paragraphMixins.lgBold};
  ${menuItemMixins.default};
  transition-duration: ${TransitionDuration.Fast};
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${FontWeight.Medium};
`;

export const PrefixAndLabelContainer = styled('div')`
  display: flex;
  flex-wrap: none;
  align-items: center;
`;

const PrefixIconContainer = styled('div')`
  display: flex;
  align-items: center;
  svg {
    height: 20px;
    width: 20px;
    color: ${Color.CoreSlate1000};
    & *[stroke] {
      stroke: ${Color.CoreSlate1000};
    }
  }
  margin-right: ${Spacing[2]};
`;

const SuffixIconContainer = styled('div')`
  display: flex;
  align-items: center;
  svg {
    height: 16px;
    width: 16px;
    color: ${Color.CoreSlate1000};
    & *[stroke] {
      stroke: ${Color.CoreSlate1000};
    }
  }
  margin-left: ${Spacing[3]};
`;
/**
 * A navbar item without hover styles. Used to contain controls such as
 * toggles.
 */
export interface ClickableItemProps
  extends Pick<
    ComponentPropsWithoutRef<'div'>,
    | 'onMouseOver'
    | 'onMouseOut'
    | 'onFocus'
    | 'onBlur'
    | 'onClick'
    | 'role'
    | 'title'
    | 'id'
    | 'className'
  > {
  disabled?: boolean;
  isVisible?: boolean;
  children: ReactChild;
  prefixIcon?: ReactElement<any>;
  suffixIcon?: ReactElement<any>;
  variant?: 'basic' | 'primary' | 'danger';
  routeTo?: string;
}

const ClickableItem = ({
  isVisible = true,
  prefixIcon,
  suffixIcon,
  children,
  routeTo,
  ...props
}: ClickableItemProps) => {
  const contents = isVisible ? (
    <Container {...props}>
      <PrefixAndLabelContainer>
        {prefixIcon != null && <PrefixIconContainer>{prefixIcon}</PrefixIconContainer>}
        {children}
      </PrefixAndLabelContainer>
      {suffixIcon != null && <SuffixIconContainer>{suffixIcon}</SuffixIconContainer>}
    </Container>
  ) : null;

  return routeTo ? <Link to={routeTo}>{contents}</Link> : contents;
};

export default ClickableItem;
